import { CheckCircle, XCircle, Calendar, CreditCard } from "lucide-react";
import dayjs from "dayjs";

type PaymentMethod =
  | "stripe.card"
  | "stripe.sepa_debit"
  | "stripe.customer_balance"
  | "stripe.paypal"
  | "stripe.revolut_pay"
  | "stripe.satispay"
  | "stripe.apple_pay"
  | "stripe.google_pay"
  | "bonifico_italia"
  | "unknown";

type MoneyDTO = {
  amount: number;
  currency: string;
};

type InstallmentInvoice = {
  createdAt: Date;
  installmentNumber: number;
  status: "failed" | "paid";
  amountPaidInCents?: MoneyDTO;
  paymentMethod?: {
    method: PaymentMethod | string;
    cardLast4Digits?: string;
  };
  failureCode?: string;
  failureMessage?: string;
};

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  "stripe.card": "Carta",
  "stripe.apple_pay": "Apple Pay",
  "stripe.google_pay": "Google Pay",
  "stripe.paypal": "PayPal",
  "stripe.satispay": "Satispay",
  "stripe.customer_balance": "Bonifico",
  "stripe.sepa_debit": "Addebito SEPA",
  "stripe.revolut_pay": "Revolut Pay",
  bonifico_italia: "Bonifico Italia (saldo rate)",
  unknown: "Sconosciuto",
};

function InstallmentInvoiceCard({ invoice }: { invoice: InstallmentInvoice }) {
  const isPaid = invoice.status === "paid";

  return (
    <div
      className={`border-2 rounded-lg p-4 transition-all ${
        isPaid ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {isPaid ? (
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
          ) : (
            <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          )}

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">
                Rata {invoice.installmentNumber}
              </span>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  isPaid ? "bg-green-600 text-white" : "bg-red-600 text-white"
                }`}
              >
                {isPaid ? "Pagata" : "Fallita"}
              </span>
            </div>

            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>{dayjs(invoice.createdAt).format("DD/MM/YYYY HH:mm")}</span>
            </div>
          </div>
        </div>

        <div className="text-right">
          {invoice.amountPaidInCents && (
            <div className="font-semibold text-gray-900">
              €{(invoice.amountPaidInCents.amount / 100).toFixed(2)}
            </div>
          )}
        </div>
      </div>

      {isPaid && invoice.paymentMethod && (
        <div className="mt-3 pt-3 border-t border-green-200">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <CreditCard className="w-4 h-4" />
            <span>
              {PAYMENT_METHOD_LABELS[
                invoice.paymentMethod.method as PaymentMethod
              ] || invoice.paymentMethod.method}
              {invoice.paymentMethod.cardLast4Digits &&
                ` - *${invoice.paymentMethod.cardLast4Digits}`}
            </span>
          </div>
        </div>
      )}

      {!isPaid && (invoice.failureCode || invoice.failureMessage) && (
        <div className="mt-3 pt-3 border-t border-red-200 text-sm text-gray-700">
          {invoice.failureCode && (
            <div>
              <span className="font-medium">Codice:</span> {invoice.failureCode}
            </div>
          )}
          {invoice.failureMessage && (
            <div>
              <span className="font-medium">Messaggio:</span>{" "}
              {invoice.failureMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function InstallmentInvoicesSection({
  installmentInvoices,
}: {
  installmentInvoices: InstallmentInvoice[];
}) {
  if (!installmentInvoices || installmentInvoices.length === 0) {
    return null;
  }

  const groupedByInstallment = installmentInvoices.reduce(
    (acc, invoice) => {
      const key = invoice.installmentNumber;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(invoice);
      return acc;
    },
    {} as Record<number, InstallmentInvoice[]>,
  );

  const sortedInstallmentNumbers = Object.keys(groupedByInstallment)
    .map(Number)
    .sort((a, b) => b - a);

  const totalPaid = installmentInvoices.filter(
    (i) => i.status === "paid",
  ).length;
  const totalFailed = installmentInvoices.filter(
    (i) => i.status === "failed",
  ).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-gray-700">
              {totalPaid} pagat{totalPaid === 1 ? "a" : "e"}
            </span>
          </div>
          {totalFailed > 0 && (
            <div className="flex items-center gap-1.5">
              <XCircle className="w-4 h-4 text-red-600" />
              <span className="text-gray-700">
                {totalFailed} fallit{totalFailed === 1 ? "a" : "e"}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {sortedInstallmentNumbers.map((installmentNumber) => {
          const invoices = groupedByInstallment[installmentNumber];

          return (
            <div key={installmentNumber} className="space-y-2">
              {invoices.length > 1 && (
                <div className="text-sm font-medium text-gray-700 mt-3">
                  Rata {installmentNumber} - {invoices.length} tentativi
                </div>
              )}

              <div className="space-y-2">
                {invoices
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime(),
                  )
                  .map((invoice, index) => (
                    <InstallmentInvoiceCard
                      key={`${installmentNumber}-${index}`}
                      invoice={invoice}
                    />
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
